// Unified pointer-based drag & drop.
//
// HTML5 drag-and-drop never fires drop events on iOS Safari, so tiles/boxes
// couldn't be dropped onto boxes or the arrangement on a phone. This module
// reimplements drag/drop on raw pointer events (one code path for mouse + touch):
//   - a source calls `makeDraggable(el, getData)`
//   - a target calls `registerDropZone(zone)`
// While a drag is active a floating ghost follows the pointer; the zone under the
// pointer (hit-tested with elementFromPoint) is highlighted and receives onDrop
// on release. DOM-only — the audio engine never sees this.

export interface DragData {
  kind: "slot" | "cell";
  /** Palette slot index (as a string) or a cell id. */
  id: string;
  /** Short text shown on the drag ghost. */
  label: string;
}

export interface DropZone {
  el: HTMLElement;
  accepts: (data: DragData) => boolean;
  onDrop: (data: DragData, clientX: number, clientY: number) => void;
  /** Optional live preview: fired on every pointer move while over this zone
   *  (and while auto-scrolling), so a zone can show where the drop will land. */
  onDragOver?: (data: DragData, clientX: number, clientY: number) => void;
  /** Optional: fired when the drag leaves this zone, drops, ends or cancels. */
  onDragLeave?: () => void;
  /** Class toggled on `el` while a compatible drag hovers (default "drag-over"). */
  overClass?: string;
}

const THRESHOLD = 6;   // px of movement before a press becomes a drag
const EDGE = 96;       // px from the top/bottom edge that triggers auto-scroll
const MAX_SPEED = 22;  // px per frame at the very edge
const zones: DropZone[] = [];

interface ActiveDrag {
  data: DragData;
  ghost: HTMLElement;
  overZone: DropZone | null;
  lastX: number;
  lastY: number;
}
let active: ActiveDrag | null = null;
let scrollRaf = 0;

// While dragging near the top/bottom of the viewport, scroll the page so
// off-screen drop targets (boxes below the fold, the arrangement) come into
// reach — the whole reason tile→box / box→canvas felt "broken" on short screens.
function autoScrollTick(): void {
  if (!active) { scrollRaf = 0; return; }
  const h = window.innerHeight;
  const y = active.lastY;
  let dy = 0;
  if (y < EDGE) dy = -Math.ceil(((EDGE - y) / EDGE) * MAX_SPEED);
  else if (y > h - EDGE) dy = Math.ceil(((y - (h - EDGE)) / EDGE) * MAX_SPEED);
  if (dy !== 0) {
    window.scrollBy(0, dy);
    hoverAt(active.lastX, active.lastY, active.data);
  }
  scrollRaf = requestAnimationFrame(autoScrollTick);
}

// Swallow the synthetic click that follows a completed drag, so a drop doesn't
// also "select"/trigger the element under the pointer. One-shot, auto-clears.
let suppressClick = false;
window.addEventListener(
  "click",
  (e) => {
    if (!suppressClick) return;
    suppressClick = false;
    e.stopPropagation();
    e.preventDefault();
  },
  true,
);

export function registerDropZone(zone: DropZone): () => void {
  zones.push(zone);
  return () => {
    const i = zones.indexOf(zone);
    if (i >= 0) zones.splice(i, 1);
  };
}

function zoneAt(clientX: number, clientY: number, data: DragData): DropZone | null {
  const hit = document.elementFromPoint(clientX, clientY);
  for (let node: Element | null = hit; node; node = node.parentElement) {
    const zone = zones.find((z) => z.el === node && z.accepts(data));
    if (zone) return zone;
  }
  return null;
}

function setOverZone(next: DropZone | null): void {
  if (!active || active.overZone === next) return;
  if (active.overZone) {
    active.overZone.el.classList.remove(active.overZone.overClass ?? "drag-over");
    active.overZone.onDragLeave?.();
  }
  if (next) next.el.classList.add(next.overClass ?? "drag-over");
  active.overZone = next;
}

// Hit-test, highlight, and feed the live drop preview of the zone under the
// pointer. Called on every move and while auto-scrolling.
function hoverAt(clientX: number, clientY: number, data: DragData): void {
  setOverZone(zoneAt(clientX, clientY, data));
  active?.overZone?.onDragOver?.(data, clientX, clientY);
}

function begin(data: DragData, x: number, y: number): void {
  const ghost = document.createElement("div");
  ghost.className = "drag-ghost";
  ghost.textContent = data.label || (data.kind === "cell" ? "box" : "sound");
  ghost.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  document.body.append(ghost);
  active = { data, ghost, overZone: null, lastX: x, lastY: y };
  scrollRaf = requestAnimationFrame(autoScrollTick);
}

function end(): void {
  if (!active) return;
  setOverZone(null);
  active.ghost.remove();
  active = null;
  if (scrollRaf) { cancelAnimationFrame(scrollRaf); scrollRaf = 0; }
}

export function makeDraggable(
  el: HTMLElement,
  getData: () => DragData | null,
  opts?: { touchAction?: string },
): void {
  // Touch handling: "none" = we own all gestures. "pan-x" lets a horizontal
  // swipe scroll the parent row while a vertical drag still picks the item up
  // (used by the scrollable sample row).
  el.style.touchAction = opts?.touchAction ?? "none";
  el.addEventListener("pointerdown", (e) => {
    if (e.button && e.button !== 0) return;
    const data = getData();
    if (!data) return;
    suppressClick = false;

    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    let started = false;

    const move = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      if (!started) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < THRESHOLD) return;
        started = true;
        begin(data, ev.clientX, ev.clientY);
        try { el.setPointerCapture(pointerId); } catch { /* ignore */ }
      }
      ev.preventDefault();
      if (!active) return;
      active.lastX = ev.clientX;
      active.lastY = ev.clientY;
      active.ghost.style.transform = `translate(${ev.clientX}px, ${ev.clientY}px) translate(-50%, -50%)`;
      hoverAt(ev.clientX, ev.clientY, data);
    };
    const finish = (ev: PointerEvent, drop: boolean): void => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      if (!started) return;
      if (drop) {
        const zone = zoneAt(ev.clientX, ev.clientY, data);
        if (zone) zone.onDrop(data, ev.clientX, ev.clientY);
      }
      // A drag happened — swallow the trailing click so we don't also select.
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 300);
      end();
    };
    const up = (ev: PointerEvent): void => finish(ev, true);
    const cancel = (ev: PointerEvent): void => finish(ev, false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  });
}
